<template>
  <div class="login-container">
    <div class="login-background">
      <div class="background-overlay"></div>
    </div>
    
    <div class="login-content">
      <div class="login-card">
        <!-- 登录表单 -->
        <div class="login-form">
          <div class="logo-section">
            <img src="@/assets/logo.png" alt="赛吧管理后台" class="login-logo" />
            <h2 class="login-title">赛吧管理后台</h2>
            <p class="login-subtitle">请输入账号密码登录系统</p>
          </div>

          <el-form
            ref="loginFormRef"
            :model="loginForm"
            :rules="loginRules"
            class="form"
            @keyup.enter="handleLogin"
          >
            <el-form-item prop="username">
              <el-input
                v-model="loginForm.username"
                placeholder="请输入用户名"
                size="large"
                prefix-icon="User"
                :disabled="loading"
              />
            </el-form-item>

            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                placeholder="请输入密码"
                size="large"
                prefix-icon="Lock"
                :disabled="loading"
                show-password
              />
            </el-form-item>

            <el-form-item prop="captcha" v-if="showCaptcha">
              <div class="captcha-input">
                <el-input
                  v-model="loginForm.captcha"
                  placeholder="请输入验证码"
                  size="large"
                  prefix-icon="Key"
                  :disabled="loading"
                  @keyup.enter="handleLogin"
                />
                <div class="captcha-image" @click="refreshCaptcha">
                  <img 
                    v-if="captchaImage" 
                    :src="captchaImage" 
                    alt="验证码" 
                    class="captcha-img"
                  />
                  <div v-else class="captcha-loading">
                    <el-icon><Loading /></el-icon>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item>
              <el-button
                type="primary"
                size="large"
                :loading="loading"
                @click="handleLogin"
                class="login-btn"
              >
                {{ loading ? '登录中...' : '登录' }}
              </el-button>
            </el-form-item>
          </el-form>

          <div class="login-footer">
            <el-checkbox v-model="rememberMe" label="记住密码" />
            <el-button link type="primary" @click="handleForgotPassword">
              忘记密码？
            </el-button>
          </div>
        </div>

        <!-- 系统信息 -->
        <div class="system-info">
          <div class="info-item">
            <el-icon><Monitor /></el-icon>
            <span>系统版本: v1.0.0</span>
          </div>
          <div class="info-item">
            <el-icon><Clock /></el-icon>
            <span>最后更新: 2023-10-15</span>
          </div>
          <div class="info-item">
            <el-icon><Service /></el-icon>
            <span>技术支持: 400-123-4567</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 版权信息 -->
    <div class="copyright">
      © 2023 赛吧运动社交平台 - 曼因路普物联科技提供技术支持
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Lock, Picture } from '@element-plus/icons-vue'
import api from '@/utils/api'

export default {
  name: 'Login',
  
  setup() {
    const router = useRouter()
    const loginFormRef = ref()
    
    const loginForm = reactive({
      username: '',
      password: '',
      captcha: ''
    })
    
    const loginRules = reactive({
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
      ],
      captcha: [
        { required: true, message: '请输入验证码', trigger: 'blur' },
        { len: 4, message: '验证码长度为4位', trigger: 'blur' }
      ]
    })
    
    const loading = ref(false)
    const rememberMe = ref(false)
    const showCaptcha = ref(false)
    const captchaImage = ref('')
    const loginAttempts = ref(0)
    
    // 检查是否已登录
    const checkLoginStatus = () => {
      const token = localStorage.getItem('admin_token')
      if (token) {
        router.push('/dashboard')
      }
    }
    
    // 加载保存的登录信息
    const loadSavedCredentials = () => {
      const savedUsername = localStorage.getItem('saved_username')
      const savedPassword = localStorage.getItem('saved_password')
      
      if (savedUsername && savedPassword) {
        loginForm.username = savedUsername
        loginForm.password = savedPassword
        rememberMe.value = true
      }
    }
    
    // 获取验证码
    const getCaptcha = async () => {
      try {
        // 调用真实API获取验证码
        const response = await api.getCaptcha()
        captchaImage.value = response.data.captchaImage
      } catch (error) {
        console.error('获取验证码失败:', error)
        ElMessage.error('获取验证码失败')
      }
    }
    
    // 刷新验证码
    const refreshCaptcha = () => {
      getCaptcha()
      loginForm.captcha = ''
    }
    
    // 处理登录
    const handleLogin = async () => {
      if (!loginFormRef.value) return
      
      try {
        const valid = await loginFormRef.value.validate()
        if (!valid) return
        
        loading.value = true
        
        // 调用真实登录API
        const response = await api.login({
          username: loginForm.username,
          password: loginForm.password,
          captcha: loginForm.captcha
        })
        
        // 保存登录信息 - 修正token获取路径
        const token = response.data?.data?.token || response.data?.token
        localStorage.setItem('admin_token', token)
        
        if (rememberMe.value) {
          localStorage.setItem('saved_username', loginForm.username)
          localStorage.setItem('saved_password', loginForm.password)
        } else {
          localStorage.removeItem('saved_username')
          localStorage.removeItem('saved_password')
        }
        
        ElMessage.success('登录成功')
        router.push('/dashboard')
        
      } catch (error) {
        console.error('登录失败:', error)
        ElMessage.error('登录失败，请重试')
      } finally {
        loading.value = false
      }
    }
    
    // 处理忘记密码
    const handleForgotPassword = () => {
      ElMessage.info('请联系系统管理员重置密码')
    }
    
    onMounted(() => {
      checkLoginStatus()
      loadSavedCredentials()
    })
    
    return {
      loginFormRef,
      loginForm,
      loginRules,
      loading,
      rememberMe,
      showCaptcha,
      captchaImage,
      handleLogin,
      refreshCaptcha,
      handleForgotPassword
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('@/assets/login-bg.jpg') center/cover no-repeat;
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
}

.login-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 16px;
  padding: 40px 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.login-form {
  margin-bottom: 30px;
}

.logo-section {
  text-align: center;
  margin-bottom: 30px;
}

.login-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
}

.login-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.login-subtitle {
  font-size: 14px;
  color: #909399;
}

.form {
  margin-bottom: 20px;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

:deep(.el-input__wrapper) {
  border-radius: 8px;
}

:deep(.el-input__prefix) {
  padding-left: 12px;
}

.captcha-input {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-image {
  width: 100px;
  height: 40px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  flex-shrink: 0;
}

.captcha-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.captcha-loading {
  color: #909399;
}

.login-btn {
  width: 100%;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  height: 48px;
}

.login-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
}

:deep(.el-checkbox__label) {
  font-size: 14px;
  color: #606266;
}

.system-info {
  border-top: 1px solid #ebeef5;
  padding-top: 20px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #909399;
}

.info-item:last-child {
  margin-bottom: 0;
}

.copyright {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  z-index: 1;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .login-content {
    padding: 15px;
    max-width: 100%;
  }
  
  .login-card {
    padding: 30px 20px;
    margin: 0 10px;
  }
  
  .login-logo {
    width: 60px;
    height: 60px;
  }
  
  .login-title {
    font-size: 20px;
  }
  
  .captcha-input {
    flex-direction: column;
    align-items: stretch;
  }
  
  .captcha-image {
    width: 100%;
    margin-top: 10px;
  }
  
  .login-footer {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}

/* 动画效果 */
.login-card {
  animation: slideUp 0.6s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 输入框聚焦效果 */
:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
}

/* 按钮悬停效果 */
.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

.login-btn:active {
  transform: translateY(0);
}
</style>